// stores/routeTripStore.js
'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam() + '&withDetail=true', payload);
  return response;
};

export const revalidateRouteTripList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/route-trip/search');
};

export const useRouteTripList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .equal('is_active', 'true')
      .setOrder(['id'], false);

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/route-trip/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};
export const useRouteTripHistoryList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .equal('is_active', 'false')
      .setOrder(['id'], false);

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/route-trip/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const useRouteTripAll = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .setOrder(['id'], false);

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/route-trip/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getRouteTripById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    // Pada gambar API list hanya ada search/post, put, delete, new.
    // Jika tidak ada endpoint khusus GET /{id}, gunakan search dengan equal
    const fb = new FilterBuilder().setPaging(0, 1).equal('id', String(id));
    const response = await fleetApi.post(`/route-trip/search${getTzParam()}`, fb.build());
    const dataArr = response?.data?.data ?? response?.data;
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (error) {
    console.error('Failed to fetch route trip by ID:', error);
    throw error;
  }
};

export const createRouteTrip = async (formData = {}) => {
  try {
    const response = await fleetApi.post(`/route-trip${getTzParam()}&translate=en&withDetail=true`, formData);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateRouteTripList();
    return response.data;
  } catch (error) {
    console.error('Error creating RouteTrip:', error);
    throw error;
  }
};

export const updateRouteTripAll = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.post(`/route-trip${getTzParam()}&translate=en&id=${id}&withDetail=true`, formData);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateRouteTripList();
    return response.data;
  } catch (error) {
    console.error('Error updating RouteTrip:', error);
    throw error;
  }
};

export const deleteRouteTrip = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/route-trip/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateRouteTripList();
    return response.data;
  } catch (error) {
    console.error('Error deleting RouteTrip:', error);
    throw error;
  }
};

export const checkIsUsedInRouteTrip = async (spatialId, spatialType) => {
  if (!spatialId || !spatialType) return { isUsed: false, routeNames: [] };
  try {
    let start = 0;
    const length = 100;
    let hasMore = true;
    const routeNames = new Set();

    while (hasMore) {
      const payload = {
        paging: { start, length },
        orders: { columns: ['id'], ascending: false },
        joins: [{ name: 'company', column_results: ['name'] }],
        columns: [{ name: 'is_active', logic_operator: '=', operator: 'AND', value: 'true' }]
      };
      const resp = await fleetApi.post(`/route-trip/search${getTzParam()}&withDetail=true`, payload);
      const dataArr = resp?.data?.data || resp?.data || [];
      if (!Array.isArray(dataArr) || dataArr.length === 0) {
        break;
      }

      for (const route of dataArr) {
        if (route.spatials && Array.isArray(route.spatials)) {
          const found = route.spatials.some((s) => String(s.spatial_id) === String(spatialId) && s.spatial_type === spatialType);
          if (found) {
            const rName = route.name || (route.join && route.join.route_trip_name) || `ID: ${route.id}`;
            routeNames.add(rName);
          }
        }
      }

      if (dataArr.length < length) {
        hasMore = false;
      } else {
        start += length;
      }
    }

    const routeNamesArray = Array.from(routeNames);
    return {
      isUsed: routeNamesArray.length > 0,
      routeNames: routeNamesArray
    };
  } catch (error) {
    console.error('Error checking route trip usage:', error);
    return { isUsed: false, routeNames: [] };
  }
};
